<template>
  <div :class="isDark ? 'bg-zinc-950 text-zinc-300' : 'bg-gray-50 text-zinc-700'" class="min-h-screen px-6 py-6 transition-colors duration-300">

    <!-- Header -->
    <div class="flex justify-between items-center border-b pb-5 mb-8 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100'">
      <div class="space-y-1 text-left">
        <h1 class="text-xl font-normal tracking-tight transition-colors duration-200"
          :class="isDark ? 'text-white' : 'text-zinc-900'">
          Prévisualisation de votre fiche
        </h1>
        <p class="text-xs font-light transition-colors duration-200"
          :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          Voici comment votre établissement apparaît sur BookMySalon pour les clients
        </p>
      </div>
      <button
        @click="router.push('/settings')"
        class="px-4 py-2 rounded-xl text-xs font-medium uppercase tracking-wider border transition-all duration-300"
        :class="isDark
          ? 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800'
          : 'bg-white border-gray-200 text-zinc-600 hover:bg-gray-50'">
        Retour aux paramètres
      </button>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-20">
      <div class="text-zinc-500">Chargement...</div>
    </div>

    <!-- Preview Content -->
    <div v-else-if="salon" class="max-w-7xl mx-auto">
      <div class="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">

        <!-- Main Content -->
        <div class="lg:col-span-8 space-y-10">

          <!-- Photos Section -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div :class="isDark ? 'bg-zinc-900 border-zinc-800/80' : 'bg-white border-gray-200'" class="md:col-span-2 relative rounded-2xl overflow-hidden border group h-80 shadow-sm">
              <img
                :src="salon.logo || salon.image || 'https://cnopt.tn/wp-content/uploads/2023/06/default-image.jpg'"
                alt="Photo principale"
                class="w-full h-full object-cover"
              />
            </div>

            <div class="grid grid-cols-4 md:grid-cols-1 md:flex md:flex-col gap-3 h-24 md:h-80 overflow-hidden">
              <div
                v-for="(photo, idx) in (salon.photos || []).slice(0, 4)"
                :key="idx"
                :class="isDark ? 'border-zinc-900 bg-zinc-900' : 'border-gray-200 bg-gray-100'"
                class="relative rounded-xl overflow-hidden border aspect-square md:aspect-auto md:flex-1 shadow-sm"
              >
                <img
                  :src="photo"
                  alt="Photo galerie"
                  class="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <!-- Title & Description -->
          <div class="text-left space-y-2">
            <h1 class="text-3xl font-light tracking-tight" :class="isDark ? 'text-white' : 'text-zinc-900'">
              {{ salon.nom_societe }}
            </h1>
          </div>

          <!-- Description Section -->
          <div v-if="salon.description" :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-gray-100 shadow-sm'" class="p-8 border rounded-2xl text-left">
            <h2 :class="isDark ? 'text-zinc-500' : 'text-zinc-400'" class="text-[10px] uppercase tracking-widest mb-4 font-semibold">À propos de l'établissement</h2>
            <p :class="isDark ? 'text-zinc-400' : 'text-zinc-600'" class="font-light text-sm sm:text-base leading-relaxed">{{ salon.description }}</p>
          </div>

          <!-- Services Section -->
          <section v-if="categories.length > 0" class="space-y-10">
            <div :class="isDark ? 'border-zinc-900' : 'border-gray-200'" class="text-left border-b pb-4">
              <h2 :class="isDark ? 'text-white' : 'text-zinc-900'" class="text-xl font-normal tracking-tight">Carte des soins & Tarifs</h2>
              <p :class="isDark ? 'text-zinc-500' : 'text-gray-400'" class="text-xs font-light mt-1">Sélectionnez vos actes techniques et réservez votre créneau.</p>
            </div>

            <div v-for="cat in categories" :key="cat._id" class="space-y-4">
              <h3 :class="isDark ? 'text-[#D5CCBC]' : 'text-zinc-800'" class="text-[11px] font-semibold uppercase tracking-widest text-left flex items-center gap-2">
                <span :class="isDark ? 'bg-[#D5CCBC]' : 'bg-zinc-700'" class="w-1 h-1 rounded-full"></span>
                {{ cat.nom }}
              </h3>

              <div v-if="cat.services && cat.services.length" :class="isDark ? 'bg-zinc-900/10 border-zinc-900 divide-zinc-900' : 'bg-white border-gray-100 divide-gray-100 shadow-sm'" class="border rounded-2xl divide-y overflow-hidden">
                <div
                  v-for="service in cat.services"
                  :key="service._id"
                  class="flex flex-col sm:flex-row justify-between sm:items-center p-5 gap-4"
                >
                  <div class="text-left space-y-1 max-w-lg">
                    <h4 :class="isDark ? 'text-white' : 'text-zinc-900'" class="text-sm font-medium">{{ service.nom }}</h4>
                    <p :class="isDark ? 'text-zinc-500' : 'text-gray-400'" class="text-xs font-light leading-relaxed">{{ service.description }}</p>
                  </div>

                  <div class="flex items-center gap-4 text-xs font-mono">
                    <span :class="isDark ? 'text-zinc-600' : 'text-gray-400'" class="flex items-center gap-1.5 font-light">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      {{ service.duree }} min
                    </span>
                    <span :class="isDark ? 'text-white' : 'text-zinc-900'" class="font-normal text-sm">{{ service.prix }} €</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div>

        <!-- Sidebar -->
        <div class="lg:col-span-4 space-y-6">

          <!-- Horaires -->
          <div :class="isDark ? 'bg-zinc-900/30 border-zinc-900' : 'bg-white border-gray-100 shadow-sm'" class="border rounded-2xl p-6 space-y-4">
            <h3 :class="isDark ? 'text-zinc-500' : 'text-gray-400'" class="text-[10px] uppercase tracking-widest font-semibold text-left">Horaires & Disponibilités</h3>
            <div class="space-y-2.5 text-xs">
              <div
                v-for="(creneaux, jour) in salon.horaires"
                :key="jour"
                :class="isDark ? 'border-zinc-900/40' : 'border-gray-50'"
                class="flex justify-between items-center py-2 border-b last:border-0"
              >
                <span :class="isDark ? 'text-zinc-400' : 'text-zinc-600'" class="capitalize font-light">{{ jour }}</span>
                <span v-if="creneaux && creneaux.length > 0" :class="isDark ? 'text-zinc-200' : 'text-zinc-900'" class="font-normal font-mono">
                  {{ creneaux.map(c => `${c.debut} - ${c.fin}`).join(', ') }}
                </span>
                <span v-else :class="isDark ? 'text-zinc-600' : 'text-gray-400'" class="font-light italic text-[11px]">Fermé</span>
              </div>
            </div>
          </div>

          <!-- Contact -->
          <div v-if="salon.telephone" :class="isDark ? 'bg-zinc-900/30 border-zinc-900' : 'bg-white border-gray-100 shadow-sm'" class="border rounded-2xl p-5 flex items-center justify-between text-xs">
            <div class="flex items-center gap-3">
              <svg :class="isDark ? 'text-zinc-500' : 'text-gray-400'" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
              <span :class="isDark ? 'text-zinc-400' : 'text-zinc-600'" class="font-light">Ligne Directe</span>
            </div>
            <span :class="isDark ? 'text-white' : 'text-zinc-900'" class="font-mono font-medium tracking-wide">{{ salon.telephone }}</span>
          </div>

          <!-- Address -->
          <div v-if="salon.adresse" :class="isDark ? 'bg-zinc-900/30 border-zinc-900' : 'bg-white border-gray-100 shadow-sm'" class="border rounded-2xl p-6 space-y-4">
            <div class="flex items-start gap-3 text-xs text-left">
              <svg :class="isDark ? 'text-zinc-500' : 'text-gray-400'" class="w-4 h-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              <p :class="isDark ? 'text-zinc-400' : 'text-zinc-600'" class="font-light leading-relaxed">{{ salon.adresse }}</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useTheme } from '@/composables/useTheme'
import api from '@/api'

const router = useRouter()
const { isDark } = useTheme()

const isLoading = ref(true)
const salon = ref(null)
const categories = ref([])

onMounted(async () => {
  try {
    // 1. Récupérer le profil pour obtenir le firmId imbriqué dans "admin"
    const profileResponse = await api.auth.getProfile()
    console.log('Profile response:', profileResponse.data)
    
    const adminData = profileResponse.data?.admin
    const firmId = adminData?.firmId

    if (!firmId) {
      console.error('Aucun firmId trouvé dans le profil admin')
      isLoading.value = false
      return
    }

    // 2. Charger les véritables données de l'établissement (Nom, Description, Adresse, Horaires)
    try {
      const firmResponse = await api.auth.getFirmInfo()
      salon.value = firmResponse.data.data || firmResponse.data
    } catch (firmError) {
      console.error('Erreur lors du chargement des paramètres de la firm:', firmError)
      // Fallback : Si l'endpoint de la firm échoue, on tente d'utiliser les horaires de l'admin
      salon.value = {
        nom_societe: "Mon Établissement", // Remplacer par une valeur par défaut ou dynamique
        horaires: adminData?.horaires || {}
      }
    }

    // 3. Récupérer les catégories et services (Modifié avec vos méthodes getAllServices / getAllCategories si nécessaire)
    try {
      const categoriesResponse = await api.services.getCategories()
      const servicesResponse = await api.services.getAll()

      const cats = categoriesResponse.data?.categories || []
      const services = servicesResponse.data?.services || []

      categories.value = cats.map(cat => ({
        ...cat,
        services: services.filter(s => s.categorieId === cat._id)
      }))
    } catch (servicesError) {
      console.error('Erreur lors du chargement des services:', servicesError)
    }

  } catch (error) {
    console.error('Erreur lors du chargement de la prévisualisation:', error)
  } finally {
    isLoading.value = false
  }
})
</script>